import { useCallback, useContext, useMemo } from 'react';
import { AnalyticsStateContext } from '../context/AnalyticsStateContext';

const EMPTY = {};

/**
 * Read/write per-widget options under analytics state (Firestore widgetOptions[widgetId]).
 * Returns no-ops when outside Analytics (context null).
 */
export const useWidgetOptionsPersistence = (widgetId) => {
  const ctx = useContext(AnalyticsStateContext);

  const saved = useMemo(() => {
    const raw = ctx?.state?.widgetOptions?.[widgetId];
    return raw && typeof raw === 'object' ? raw : EMPTY;
  }, [ctx?.state?.widgetOptions, widgetId]);

  const setWidgetOptions = ctx?.setWidgetOptions;
  const persist = useCallback(
    (partial) => {
      if (!setWidgetOptions || !widgetId) return;
      setWidgetOptions(widgetId, partial);
    },
    [setWidgetOptions, widgetId]
  );

  return {
    saved,
    persist,
    /**
     * Wait until the first analytics `getDoc` has settled (`isPending` false). Using `isFetched` alone
     * was insufficient: v5 `isLoading` is `isPending && isFetching`, so it could be false while the
     * query was still pending with no data, letting charts default widget state and overwrite Firestore.
     */
    isReady: !ctx || !ctx.isPending,
    /** Alias for `isPending` — true until the first analytics document load completes. */
    isLoading: ctx?.isPending ?? false,
    isPending: ctx?.isPending ?? false,
    /** True when Analytics page mounted the provider */
    isEnabled: !!ctx,
  };
};

export default useWidgetOptionsPersistence;
